import dayjs from 'dayjs';
import { apiFetch } from './interceptor';
const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/v1';

export const gamesApi = {
  getGame: async (token, id) => {
    try {
      const response = await apiFetch(`${BASE_URL}/games/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Check if we got a redirected response (expired token)
      if (response && response.redirected) {
        console.log('Token expired during game fetch');
        return { redirected: true };
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching game:', error);
      throw error;
    }
  },

  getUserGames: async (token) => {
    try {
      const response = await apiFetch(`${BASE_URL}/games/user`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Check if we got a redirected response (expired token)
      if (response && response.redirected) {
        console.log('Token expired during user games fetch');
        return { redirected: true };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching user games:', error);
      throw error;
    }
  },

  getUserGame: async (token, id) => {
    try {
      const response = await apiFetch(`${BASE_URL}/games/user/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Check if we got a redirected response (expired token)
      if (response && response.redirected) {
        console.log('Token expired during user game fetch');
        return { redirected: true };
      }

      const data = await response.json();
      return { status: response.status, data };
    } catch (error) {
      console.error('Error fetching user game:', error);
      throw error;
    }
  },

  getGameImage: async (name) => {
    const response = await apiFetch(`../assets/images/${name}.png`);
    return response.json();
  },

  getAllResults: async (token) => {
    const resultURL = new URL(`${BASE_URL}/gameResults/getAllResults`);
    resultURL.searchParams.set('drawDate', dayjs().format('YYYY-MM-DD'));

    const response = await apiFetch(resultURL, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  addGameResult: async (token, payload) => {
    const response = await apiFetch(`${BASE_URL}/gameResults`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    return { status: response.status, data };
  },

  deleteGameResult: async (token, id) => {
    const response = await apiFetch(`${BASE_URL}/gameResults/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = response.status === 204 ? null : await response.json();
    return { status: response.status, data };
  },

  createTransaction: async (token, payload) => {
    const response = await apiFetch(`${BASE_URL}/transactions`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    return { status: response.status, data };
  },

  ownedGames: async (token) => {
    const response = await apiFetch(`${BASE_URL}/games/own`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await response.json();
    return { status: response.status, data };
  },

  updateGame: async (token, id, payload) => {
    const response = await apiFetch(`${BASE_URL}/games/${id}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    return { status: response.status, data };
  },

  createGame: async (token, payload) => {
    const response = await apiFetch(`${BASE_URL}/games`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    return { status: response.status, data };
  },

  deleteGame: async (token, id) => {
    const response = await apiFetch(`${BASE_URL}/games/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { status: response.status };
  },

  // game settings
  getAllGames: async (token) => {
    const response = await apiFetch(`${BASE_URL}/gameSettings/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await response.json();
    return { status: response.status, data };
  },

  // public game settings
  getPublicGames: async () => {
    const response = await apiFetch(`${BASE_URL}/games/public`);
    const data = await response.json();
    return data;
  },

  addGameSettings: async (token, payload) => {
    const response = await apiFetch(`${BASE_URL}/gameSettings`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    return { status: response.status, data };
  },

  updateGameSettings: async (token, id, payload) => {
    const response = await apiFetch(`${BASE_URL}/gameSettings/${id}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    return { status: response.status, data };
  },

  // game results
  fetchGameResults: async (
    token,
    drawDate = dayjs().format('YYYY-MM-DD'),
    gameId = ''
  ) => {
    let url = `${BASE_URL}/gameResults?drawDate=${drawDate}`;
    if (gameId) {
      url += `&gameId=${gameId}`;
    }
    const response = await apiFetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await response.json();
    return { status: response.status, data };
  },

  fetchSpecificUserGameResults: async (
    token,
    drawDate = dayjs().format('YYYY-MM-DD'),
    gameId = ''
  ) => {
    let url = `${BASE_URL}/gameResults/user-specific?drawDate=${drawDate}`;
    if (gameId) {
      url += `&gameId=${gameId}`;
    }
    const response = await apiFetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await response.json();
    return { status: response.status, data };
  },

  // game results
  fetchAllGameResults: async (
    drawDate = dayjs().format('YYYY-MM-DD'),
    gameId = ''
  ) => {
    let url = `${BASE_URL}/gameResults/getAllResults?drawDate=${drawDate}`;
    if (gameId) {
      url += `&gameId=${gameId}`;
    }
    const response = await apiFetch(url);
    const data = await response.json();
    return { status: response.status, data };
  },

  // get game results by id
  fetchGameResultsById: async (token, id, page = 1, limit = 20) => {
    const response = await apiFetch(
      `${BASE_URL}/gameResults/${id}?page=${page}&limit=${limit}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const data = await response.json();
    return { status: response.status, data };
  },

  // Default game packages for players (role 9)
  getDefaultGamePackages: async (token) => {
    const response = await apiFetch(`${BASE_URL}/games/default-packages`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await response.json();
    return { status: response.status, data };
  },

  assignDefaultPackageToPlayers: async (token, packageId) => {
    const response = await apiFetch(
      `${BASE_URL}/games/assign-default-package/${packageId}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    const data = await response.json();
    return { status: response.status, data };
  },

  // User specific game creation
  createUserSpecificGame: async (token, userId, payload) => {
    const response = await apiFetch(
      `${BASE_URL}/games/user-specific/${userId}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );
    const data = await response.json();
    return { status: response.status, data };
  },

  // get home page games
  getHomePageGames: async () => {
    const response = await apiFetch(`${BASE_URL}/games/public-homepage`);
    const data = await response.json();
    return { status: response.status, data };
  },
};
